import type { MatchConditions, RawRuleFrom, Subject } from '@casl/ability';
import { Ability, AbilityBuilder, subject as on } from '@casl/ability';
// eslint-disable-next-line import-js/no-unresolved
import { permittedFieldsOf } from '@casl/ability/extra';
import {
  allRoles,
  baseRoles,
  membersRoles,
  possibleRoles,
  rolesDescriptions,
} from './src/roles';

interface User {
  id: string;
  internalId: number;
  partners: string[];
}

type PossibleAbilities = [string, Subject];
type AppAbility = Ability<PossibleAbilities, MatchConditions>;
type AbilityCan = AppAbility['can'];
type AbilityRule = RawRuleFrom<PossibleAbilities, MatchConditions>;
// eslint-disable-next-line no-unused-vars
type AbilitiesFn = (user: User, builder: AbilityBuilder<AppAbility>) => void;

interface PartnerAbilities {
  [Key: string]: AbilityRule[];
}

const createAppAbility = (rules: AbilityRule[] = []): AppAbility =>
  new Ability<PossibleAbilities, MatchConditions>(rules, {
    fieldMatcher: (fields) => (field) => fields.includes(field),
    conditionsMatcher: (conditions: MatchConditions) => conditions,
  });

const userRules = (
  partnerId: string,
  globalAbilities: AbilityRule[] = [],
  partnerAbilities: PartnerAbilities = {},
): AbilityRule[] => {
  const rules = [];

  if (globalAbilities.length) {
    rules.push(...globalAbilities);
  }

  if (partnerId && partnerAbilities[partnerId]) {
    rules.push(...partnerAbilities[partnerId]);
  }

  return rules;
};

export default {
  forUser(
    globalAbilities: AbilityRule[],
    partnerAbilities: PartnerAbilities,
    partnerId: string,
  ) {
    return createAppAbility(
      userRules(partnerId, globalAbilities, partnerAbilities),
    );
  },

  forRole(user: User, abilities: AbilitiesFn) {
    const builder = new AbilityBuilder<AppAbility>(createAppAbility);
    abilities?.(user, builder);
    return builder.build();
  },
};

export {
  type AbilityCan,
  type AbilityRule,
  type PartnerAbilities,
  allRoles,
  baseRoles,
  createAppAbility,
  membersRoles,
  on,
  permittedFieldsOf,
  possibleRoles,
  rolesDescriptions,
};
